import { Link } from "react-router-dom";
import { ChevronRight, Check, Sparkles } from "lucide-react";
import { SidebarAd, BannerAd } from "@/components/AdSlots";
import { ToolCard } from "@/components/ToolCard";
import { getCategory, liveToolsByCategory } from "@/lib/toolsData";
import { useSeo } from "@/lib/useSeo";

export const ToolLayout = ({ tool, children }) => {
  const category = getCategory(tool.category);
  const related = liveToolsByCategory(tool.category).filter((t) => t.slug !== tool.slug).slice(0, 4);
  const Icon = tool.icon;

  const features = [
    "100% free — no signup or watermarks",
    "Fast, browser-first processing",
    "Works on desktop and mobile",
    "Your files stay private",
  ];
  const steps = [
    `Open the ${tool.name} tool below.`,
    "Add your input or upload your file.",
    "Adjust any options if needed.",
    "Get your result instantly — copy or download it.",
  ];
  const faqs = [
    { q: `Is ${tool.name} free?`, a: `Yes, ${tool.name} is completely free to use with no limits.` },
    { q: "Do you store my files?", a: "No. Files are processed securely and are never stored on our servers." },
    { q: "Which devices are supported?", a: "Any modern browser on desktop, tablet or mobile." },
  ];

  useSeo({
    title: `${tool.name} — ${tool.description}`,
    description: `${tool.description} Free online ${tool.name} by Kovi Tools — fast, private and no signup required.`,
    path: `/tool/${tool.slug}`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "SoftwareApplication",
      name: tool.name, applicationCategory: "UtilitiesApplication", operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description: tool.description,
    },
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 animate-fade-up">
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/category/${category.id}`} className="hover:text-foreground">{category.name}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="font-medium text-foreground">{tool.name}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Icon className="h-6 w-6" />
            </span>
            <div>
              <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{tool.name}</h1>
              <p className="text-sm text-muted-foreground">{tool.description}</p>
            </div>
          </div>

          <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            {children}
          </div>

          <div className="mt-8"><BannerAd /></div>

          {/* Features */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold tracking-tight">Features</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {features.map((f) => (
                <div key={f} className="flex items-start gap-2.5 rounded-xl border border-border bg-card p-4">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="text-sm">{f}</span>
                </div>
              ))}
            </div>
          </section>

          {/* How to use */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold tracking-tight">How to use {tool.name}</h2>
            <ol className="mt-4 space-y-3">
              {steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{i + 1}</span>
                  <span className="text-sm text-muted-foreground">{s}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold tracking-tight">FAQ</h2>
            <div className="mt-4 space-y-3">
              {faqs.map((f) => (
                <div key={f.q} className="rounded-xl border border-border bg-card p-5">
                  <p className="font-semibold">{f.q}</p>
                  <p className="mt-1.5 text-sm text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {related.length > 0 && (
            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold tracking-tight">Related tools</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {related.map((t) => <ToolCard key={t.slug} tool={t} />)}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-6">
            <SidebarAd />
            <div className="rounded-2xl border border-border bg-primary p-6 text-primary-foreground">
              <Sparkles className="h-5 w-5" />
              <p className="mt-3 font-heading text-lg font-bold leading-snug">Free forever. No signup, no watermarks, no limits.</p>
            </div>
            <SidebarAd />
          </div>
        </aside>
      </div>
    </div>
  );
};
